/**
 * One control that knows what kind of field it is editing.
 *
 * Every form that edits a document value renders this: the desk's review
 * list and the guest's field sheet. (The page itself does not - there a value
 * is the document's own text, edited in place; see `DocumentViewer`.)
 * Before it existed each screen built its own `<input>`, and the differences
 * between them were bugs rather than choices — a `max_length` the tablet
 * enforced and the desk did not, an `options` list neither honoured, and a
 * `type="date"` that silently swallowed anything that was not ISO.
 *
 * The rule that keeps a value visible: **never hand a native picker a value it
 * will refuse.** A `<input type="date">` given `15-04-24` renders an empty
 * `mm/dd/yyyy`, so the receptionist sees a blank box behind a green confidence
 * badge, cannot tell what the scanner read, and cannot correct it. Dates are
 * now a DD/MM/YYYY text box (`DateInput`) that shows a value it cannot read as
 * it is, and `pickerFallbackNote` says why, so the value is always both
 * visible and editable.
 */

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import { fieldStyles } from '@/components/ui/primitives'
import { DateInput } from '@/components/ui/DateInput'

export interface FieldSpec {
  field_key: string
  data_type: string
  max_length?: number | null
  options?: string[] | null
  placeholder?: string | null
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** Would a native picker accept this value, or would it blank it? */
export function pickerAccepts(dataType: string, value: string): boolean {
  if (!value) return true
  if (dataType === 'date') return ISO_DATE.test(value)
  if (dataType === 'datetime') return ISO_DATETIME.test(value)
  return true
}

export function inputModeFor(dataType: string): 'numeric' | 'decimal' | 'email' | 'tel' | 'text' {
  switch (dataType) {
    case 'integer':
      return 'numeric'
    case 'decimal':
      return 'decimal'
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    default:
      return 'text'
  }
}

interface Props {
  id?: string
  spec: FieldSpec
  value: string
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
}

export const FieldControl = forwardRef<HTMLInputElement | HTMLSelectElement, Props>(
  function FieldControl(
    { id, spec, value, onChange, onFocus, onBlur, onKeyDown, disabled, autoFocus, className, ...aria },
    ref,
  ) {
    const shared = {
      id,
      disabled,
      autoFocus,
      onFocus,
      onBlur,
      onKeyDown,
      className: cn(
        'h-9 w-full px-3 text-sm',
        fieldStyles,
        'border-[var(--border-strong)]',
        className,
      ),
      ...aria,
    }

    // A closed list is a list. The value the scanner read is kept as an option
    // of its own when it is not one of them, so an off-list read is visible
    // and can be replaced rather than silently rewritten on first render.
    if (spec.options?.length) {
      const offList = value && !spec.options.includes(value)
      return (
        <select
          {...shared}
          ref={ref as React.Ref<HTMLSelectElement>}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">—</option>
          {offList && <option value={value}>{value} (as read)</option>}
          {spec.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )
    }

    // Dates are typed and shown DD/MM/YYYY whatever the browser's locale; the
    // value itself stays ISO. See `DateInput`.
    if (spec.data_type === 'date' || spec.data_type === 'datetime') {
      return (
        <DateInput
          {...shared}
          ref={ref as React.Ref<HTMLInputElement>}
          value={value}
          onChange={onChange}
          withTime={spec.data_type === 'datetime'}
        />
      )
    }

    return (
      <input
        {...shared}
        ref={ref as React.Ref<HTMLInputElement>}
        type="text"
        value={value}
        inputMode={inputModeFor(spec.data_type)}
        maxLength={spec.max_length ?? undefined}
        placeholder={spec.placeholder ?? undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    )
  },
)

/** The note that explains a text box standing in for a date picker. */
export function pickerFallbackNote(spec: FieldSpec, value: string): string | null {
  if (pickerAccepts(spec.data_type, value)) return null
  return spec.data_type === 'datetime'
    ? 'Not a recognised date and time — type it as DD/MM/YYYY HH:MM, or pick it from the calendar.'
    : 'Not a recognised date — type it as DD/MM/YYYY, or pick it from the calendar.'
}
